import { Component, OnDestroy, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { AuthService, UserDto } from '../../../services/auth/auth';
import { LanguageService, LanguageCode } from '../../../services/i18n/language';
import { NotificationBellComponent } from '../notification-bell/notification-bell';
import { CurrencyRatesComponent } from '../currency-rates/currency-rates.component';
import { CurrencyRatesService } from '../../../services/currency-rates';
import { CALC_SECTIONS, CalcSection, DEFAULT_CALC_SECTION } from '../../shared/customs-calculations/calculation-sections';
import { CalculationProgressService } from '../../../services/calculation-progress';
import { PhoneClaimService } from '../../../services/phone-claim';
import { CompanyContextService } from '../../../services/company-context';
import { CompanyPermission } from '../../../services/api/company';

interface NavItem {
  route: string;
  labelKey: string;
  fallback: string;
  icon: 'users' | 'chart' | 'search' | 'bids' | 'deals' | 'list' | 'plus' | 'supplier' | 'transport' | CalcSection;
  /** Гость: раздел закрыт до ввода телефона. */
  locked?: boolean;
  /** Отделить чертой от пунктов выше (например, «Мои расчёты» под разделами расчёта). */
  divider?: boolean;
  /** Главное действие раздела — отдельной заметной кнопкой внизу меню (например, «Поиск запросов»). */
  cta?: boolean;
  /** Пункт виден только при этом праве в компании (пока права не загружены, виден всем). */
  permission?: CompanyPermission;
  /** Пункт подсвечен на страницах, адрес которых подходит под это условие. */
  isActive: (url: string) => boolean;
}

/** Карточка заявки открывается и из поиска, и из ставок (?returnTo=dashboard) — подсвечиваем тот раздел, откуда пришли. */
const fromBids = (url: string) => url.includes('returnTo=dashboard');

/** Меню по ролям; пока каркас включён только для админки, остальные роли добавятся по мере переноса кабинетов. */
const NAV: Record<string, NavItem[]> = {
  ROLE_ADMIN: [
    { route: '/admin/users', labelKey: 'platform.nav.users', fallback: 'Пользователи', icon: 'users', isActive: url => url.startsWith('/admin/users') },
    { route: '/admin/stats', labelKey: 'platform.nav.stats', fallback: 'Статистика посетителей', icon: 'chart', isActive: url => url.startsWith('/admin/stats') }
  ],
  ROLE_PUBLIC: [
    { route: '/public/dashboard', labelKey: 'calcSections.myCalculations', fallback: 'Мои расчёты', icon: 'list', isActive: url => url.startsWith('/public/dashboard') },
    { route: '/calculations/new', labelKey: 'myCalcs.new', fallback: 'Новый расчёт', icon: 'plus', isActive: url => url.startsWith('/calculations/new') }
  ],
  ROLE_FREIGHT_FORWARDER: [
    {
      route: '/freight-forwarder/overview', labelKey: 'ffNav.overview', fallback: 'Сводка', icon: 'chart',
      isActive: url => url.startsWith('/freight-forwarder/overview')
    },
    {
      route: '/freight-forwarder/dashboard/deals', labelKey: 'freightForwarder.dashboard.dealsTab', fallback: 'Мои сделки', icon: 'deals', permission: 'VIEW_DEALS',
      isActive: url => url.startsWith('/freight-forwarder/dashboard/deals') || url.startsWith('/freight-forwarder/deal/')
    },
    {
      route: '/freight-forwarder/dashboard/bids', labelKey: 'freightForwarder.dashboard.bidsTab', fallback: 'Мои ставки', icon: 'bids', permission: 'PLACE_BIDS',
      isActive: url => url.startsWith('/freight-forwarder/dashboard/bids') || url.startsWith('/freight-forwarder/edit-bid')
        || (url.startsWith('/freight-forwarder/request/') && fromBids(url))
    },
    {
      route: '/freight-forwarder/counterparties', labelKey: 'ffNav.counterparties', fallback: 'Контрагенты', icon: 'users',
      isActive: url => url.startsWith('/freight-forwarder/counterparties')
    },
    {
      route: '/company/members', labelKey: 'company.members.title', fallback: 'Сотрудники', icon: 'users', permission: 'MANAGE_MEMBERS',
      isActive: url => url.startsWith('/company/members')
    },
    {
      route: '/freight-forwarder/requests', labelKey: 'header.searchOrders', fallback: 'Поиск запросов', icon: 'search', permission: 'PLACE_BIDS', cta: true,
      isActive: url => url.startsWith('/freight-forwarder/requests') || url.startsWith('/freight-forwarder/create-bid')
        || (url.startsWith('/freight-forwarder/request/') && !fromBids(url))
    }
  ],
  ROLE_CARGO_OWNER: [
    {
      route: '/cargo-owner/overview', labelKey: 'coNav.overview', fallback: 'Главная', icon: 'chart',
      isActive: url => url.startsWith('/cargo-owner/overview')
    },
    {
      route: '/cargo-owner/dashboard/supplies', labelKey: 'coNav.supplies', fallback: 'Мои поставки', icon: 'payments', permission: 'MANAGE_REQUESTS',
      isActive: url => url.startsWith('/cargo-owner/dashboard/supplies') || url.startsWith('/cargo-owner/supplies')
    },
    {
      route: '/cargo-owner/dashboard/requests', labelKey: 'coNav.freightRequests', fallback: 'Запросы на перевозку', icon: 'list', permission: 'MANAGE_REQUESTS',
      isActive: url => url.startsWith('/cargo-owner/dashboard/requests') || url.startsWith('/cargo-owner/request/')
        || url.startsWith('/cargo-owner/bid/') || url.startsWith('/cargo-owner/edit-request') || url.startsWith('/cargo-owner/create-request')
    },
    {
      route: '/cargo-owner/dashboard/deals', labelKey: 'coNav.deals', fallback: 'Сделки', icon: 'freight', permission: 'VIEW_DEALS',
      isActive: url => url.startsWith('/cargo-owner/dashboard/deals') || url.startsWith('/cargo-owner/deal/')
    },
    {
      route: '/cargo-owner/dashboard/calculations', labelKey: 'coNav.calculations', fallback: 'Расчёты', icon: 'overview', permission: 'MANAGE_REQUESTS',
      isActive: url => url.startsWith('/cargo-owner/dashboard/calculations')
    },
    {
      route: '/cargo-owner/counterparties', labelKey: 'ffNav.counterparties', fallback: 'Контрагенты', icon: 'users',
      isActive: url => url.startsWith('/cargo-owner/counterparties')
    },
    {
      route: '/company/members', labelKey: 'company.members.title', fallback: 'Сотрудники', icon: 'users', permission: 'MANAGE_MEMBERS',
      isActive: url => url.startsWith('/company/members')
    }
  ]
};

/** Список расчётов: у нового пользователя (по телефону) и у грузовладельца он в разных кабинетах. */
const MY_CALCULATIONS: Record<string, { route: string; labelKey: string; fallback: string }> = {
  ROLE_PUBLIC: { route: '/public/dashboard', labelKey: 'calcSections.myCalculations', fallback: 'Мои расчёты' },
  ROLE_CARGO_OWNER: { route: '/cargo-owner/dashboard/calculations', labelKey: 'calcSections.myCalculations', fallback: 'Мои расчёты' }
};

/** Меню страницы расчёта: его разделы и возврат к списку расчётов. */
function calculationNav(id: string, roleCode: string, processing = false): NavItem[] {
  const base = '/calculations/' + id;
  const path = (url: string) => url.split(/[?#]/)[0];
  // Пока ИИ разбирает товары, остальные разделы пусты — показываем только сводку
  const sections = processing ? CALC_SECTIONS.filter(s => s.id === DEFAULT_CALC_SECTION) : CALC_SECTIONS;
  const items: NavItem[] = sections.map(s => ({
    route: base + '/' + s.id,
    labelKey: s.labelKey,
    fallback: s.fallback,
    icon: s.id,
    isActive: (url: string) => path(url) === base + '/' + s.id || (s.id === DEFAULT_CALC_SECTION && path(url) === base)
  }));
  const list = MY_CALCULATIONS[roleCode];
  if (list) {
    items.push({ route: list.route, labelKey: list.labelKey, fallback: list.fallback, icon: 'list', divider: true, isActive: () => false });
  }
  return items;
}

/**
 * Каркас платформы (личный кабинет): тёмное меню слева, рабочая область справа.
 * Включается маршрутами с data.layout = 'platform' вместо шапки и футера сайта.
 */
@Component({
  selector: 'app-platform-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, NotificationBellComponent, CurrencyRatesComponent],
  templateUrl: './platform-shell.component.html',
  styleUrl: './platform-shell.component.scss'
})
export class PlatformShellComponent implements OnInit, OnDestroy {
  user: UserDto | null = null;
  nav: NavItem[] = [];
  menuOpen = false;
  langOpen = false;
  sideLangOpen = false;
  currentLanguage: LanguageCode = 'ru';

  readonly languages: { code: LanguageCode; label: string }[] = [
    { code: 'kk', label: 'KZ' },
    { code: 'ru', label: 'RU' },
    { code: 'en', label: 'EN' },
    { code: 'zh', label: 'ZH' }
  ];

  private subscriptions = new Subscription();

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private currencyRates: CurrencyRatesService,
    private calculationProgress: CalculationProgressService,
    private phoneClaim: PhoneClaimService,
    private companyContext: CompanyContextService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  /** Обычные пункты меню и главное действие (отдельно внизу). */
  get mainNav(): NavItem[] {
    return this.nav.filter(i => !i.cta);
  }

  get ctaNav(): NavItem[] {
    return this.nav.filter(i => i.cta);
  }

  isActive(item: NavItem): boolean {
    return item.isActive(this.router.url);
  }

  get languageLabel(): string {
    return this.languages.find(l => l.code === this.currentLanguage)?.label ?? 'RU';
  }

  get initials(): string {
    const parts = (this.user?.fullName || this.user?.email || '?').trim().split(/\s+/);
    return parts.slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('');
  }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.updateNav();
    // Курсы нужны и для показа, и для пересчёта сумм в тенге на страницах кабинета
    this.currencyRates.start();

    this.companyContext.ensureLoaded();
    this.subscriptions.add(
      this.companyContext.company$.subscribe(() => {
        this.updateNav();
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.languageService.getCurrentLanguage().subscribe(lang => {
        this.currentLanguage = lang;
        this.cdr.markForCheck();
      })
    );
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => this.cdr.markForCheck())
    );
    // Расчёт создан или готов — пересобираем меню (появляются остальные разделы)
    this.subscriptions.add(
      this.calculationProgress.changed$.subscribe(() => {
        this.updateNav();
        this.cdr.markForCheck();
      })
    );
    // Гость ввёл телефон — разделы открываются
    this.subscriptions.add(
      this.phoneClaim.claimed$.subscribe(() => {
        this.user = this.authService.getUser();
        this.updateNav();
        this.cdr.markForCheck();
      })
    );
    // На телефоне меню закрывается после перехода
    this.subscriptions.add(
      this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
        this.menuOpen = false;
        this.user = this.authService.getUser();
        this.updateNav();
      })
    );
  }

  /** На странице расчёта меню — его разделы, на остальных — пункты кабинета по роли. */
  private updateNav(): void {
    const role = this.user?.role?.code ?? '';
    const calc = this.router.url.match(/^\/calculations\/(\d+)/);
    const nav = calc ? calculationNav(calc[1], role, this.calculationProgress.isActive(+calc[1])) : (NAV[role] ?? []);
    // Гость (без телефона) видит только сводку расчёта; остальное открывается после ввода телефона.
    // Пункты NAV общие для всех — меняем копии, иначе замок остаётся и после входа по номеру
    const guest = this.authService.isGuest();
    this.companyContext.ensureLoaded();
    this.nav = nav.filter(item => !item.permission || (item.permission === 'MANAGE_MEMBERS' ? this.companyContext.canManageMembers() : this.companyContext.can(item.permission))).map(item => ({
      ...item,
      locked: guest && item.icon !== DEFAULT_CALC_SECTION && item.icon !== 'plus'
    }));
  }

  /** Закрытый пункт: просим телефон и после ввода ведём в нужный раздел. */
  openLocked(item: NavItem): void {
    this.menuOpen = false;
    this.phoneClaim.open(item.route);
  }

  ngOnDestroy(): void {
    this.currencyRates.stop();
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:keydown.escape')
  closeMenus(): void {
    this.menuOpen = false;
    this.langOpen = false;
    this.sideLangOpen = false;
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  setLanguage(code: LanguageCode): void {
    this.languageService.setLanguage(code);
    this.langOpen = false;
    this.sideLangOpen = false;
  }

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
